
import { useState, useEffect } from 'react';
import { cn } from "@/lib/utils";

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
}

const OptimizedImage = ({ src, alt, className }: OptimizedImageProps) => {
  const [imageUrl, setImageUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setError(false);

    // Déterminer l'URL initiale
    const initialUrl = src.startsWith('http') 
      ? src 
      : src.startsWith('public/') 
        ? src.replace('public/', '/') 
        : src.startsWith('/') 
          ? src 
          : `/${src}`;

    setImageUrl(initialUrl);

    // Précharger l'image
    const img = new Image();
    img.src = initialUrl;
    
    img.onload = () => {
      setIsLoading(false);
      setError(false);
    };

    img.onerror = () => {
      setIsLoading(false);
      setError(true);
      
      if (!src.startsWith('http') && !initialUrl.includes('/lovable-uploads/')) {
        const retryUrl = `/lovable-uploads/${src.split('/').pop()}`;
        const retryImg = new Image();
        retryImg.src = retryUrl;
        retryImg.onload = () => {
          setImageUrl(retryUrl);
          setIsLoading(false);
          setError(false);
        };
        retryImg.onerror = () => {
          setError(true);
          setIsLoading(false);
        };
      }
    };

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  if (isLoading) {
    return (
      <div className={cn(
        "bg-gray-200 animate-pulse", 
        className
      )} />
    );
  }

  if (error) {
    return (
      <div className={cn(
        "bg-gray-100 flex items-center justify-center",
        className
      )}>
        <svg
          className="w-12 h-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      className={className}
      loading="lazy"
      onError={(e) => {
        e.currentTarget.src = '/placeholder.svg';
      }}
    />
  );
};

export default OptimizedImage;
